import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DocumentPageListItem } from '@avisoma/shared';
import type { DocumentReconstructedDocumentsListItem } from '@avisoma/shared';
import { buildLineage, type LineageLeaf, type Lineage } from './lineageLayout';
import { LineagePopover, type PopoverContent } from './LineagePopover';
import { SlotCell, LeafCellNode, BandNode, ConnectorLine } from './LineageMapNodes';

interface LineageMapProps {
  pages: DocumentPageListItem[];
  entities: DocumentReconstructedDocumentsListItem[];
}

const MAX_CELL = 40;
const PAD = 12;
const SVG_H = 130;

// Stable color per classification label (mid-tone hsl reads in light + dark).
function clsColor(label?: string | null): string {
  if (!label) return 'hsl(220 9% 60%)';
  let h = 0;
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 58% 50%)`;
}
// Muted fill for low-certainty reconstructed docs (white band text still reads).
const MUTED_FILL = 'hsl(220 9% 62%)';
const LOW_CERTAINTY = 0.5;
const trunc = (s: string, max: number) => (s.length > max ? s.slice(0, Math.max(1, max - 1)) + '…' : s);
const topClass = (p: DocumentPageListItem): string | null => p.processing?.classification?.result?.[0]?.[0] ?? null;

export function LineageMap({ pages, entities }: LineageMapProps) {
  const navigate = useNavigate();
  const [hover, setHover] = useState<{ cols: Set<number>; popover: PopoverContent } | null>(null);

  const pageById = useMemo(() => new Map(pages.map((p) => [p.id, p])), [pages]);
  const entityById = useMemo(() => new Map(entities.map((e) => [e.reconstructedDocumentId, e])), [entities]);

  const lin: Lineage = useMemo(() => {
    const leaves: LineageLeaf[] = pages.map((p) => ({
      id: p.id,
      parentPageId: p.parentPageId,
      heading: p.sectionBounds?.heading,
      classification: topClass(p),
    }));
    const ents = entities.map((e) => ({
      reconstructedDocumentId: e.reconstructedDocumentId,
      classification: e.classification,
      suggestedFilename: e.suggestedFilename,
      pageIds: e.pageIds,
    }));
    return buildLineage(leaves, ents);
  }, [pages, entities]);

  // Adaptive cell width: roomy for small docs, compressed (still scrollable) for
  // big ones so a 160-segment doc isn't a 6000px scroll. Labels hide below ~26px.
  const CELL = Math.max(16, Math.min(MAX_CELL, Math.round(1100 / Math.max(1, lin.columnCount))));
  const showCellText = CELL >= 26;
  const bucketed = lin.bucketed;
  const width = PAD * 2 + lin.columnCount * CELL;

  // cmd/ctrl-click (or middle-click) opens in a new tab; plain click navigates in place.
  const open = useCallback(
    (path: string, e: MouseEvent) => {
      if (e.metaKey || e.ctrlKey || e.button === 1) window.open(path, '_blank', 'noopener');
      else navigate(path);
    },
    [navigate],
  );

  // Stable geometry + display descriptors — recomputed only when the layout or cell width
  // changes, never on hover. Passing these (all primitives / stable array refs) plus the
  // stable callbacks below keeps the memoized nodes inert unless their `dimmed` flips.
  const geom = useMemo(() => {
    const x = (col: number) => PAD + col * CELL;
    const cx = (col: number) => x(col) + CELL / 2;
    const connectors = bucketed ? [] : lin.cells.map((c) => ({ col: c.col, cx: cx(c.col), cols: [c.col] }));
    const slots = lin.slots.map((s) => {
      const rectW = (s.lastCol - s.firstCol + 1) * CELL - 4;
      return {
        rectX: x(s.firstCol) + 2,
        rectW,
        textX: x(s.firstCol) + rectW / 2 + 2,
        segmented: s.segmented,
        dividerXs: s.segmented ? s.childCols.slice(1).map((col) => x(col)) : [],
        pageNo: s.pageNo,
        cols: s.childCols,
      };
    });
    const cells = lin.cells.map((c) => ({
      col: c.col,
      rectX: x(c.col) + 2,
      rectW: CELL - 4,
      textX: cx(c.col),
      isSegment: c.isSegment,
      label: trunc(c.heading || c.classification || '', Math.floor(CELL / 6)),
      cols: [c.col],
    }));
    const bands = lin.bands.map((b) => {
      const rectW = (b.lastCol - b.firstCol + 1) * CELL - 4;
      const certainty = entityById.get(b.reconstructedDocumentId)?.classificationCertainty;
      const muted = certainty != null && certainty < LOW_CERTAINTY;
      const count = b.count ?? b.cols.length;
      return {
        rectX: x(b.firstCol) + 2,
        rectW,
        textX: x(b.firstCol) + rectW / 2 + 2,
        fill: muted ? MUTED_FILL : clsColor(b.classification),
        showText: !muted && !bucketed,
        label: trunc(`${b.classification}${count > 1 ? ` ·${count}` : ''}`, Math.max(4, Math.floor(rectW / 6.2))),
        cols: b.cols,
      };
    });
    return { connectors, slots, cells, bands };
  }, [lin, CELL, entityById, bucketed]);

  const isDimmed = useCallback(
    (cols: number[]) => hover != null && !cols.some((c) => hover.cols.has(c)),
    [hover],
  );

  // Hover handlers — stable across hovers (deps are data/size, not hover state). Each builds
  // its popover from the node's id/index so the memoized node only needs a numeric identity.
  const onLeafEnter = useCallback(
    (col: number, e: MouseEvent) => {
      const cell = lin.cells[col];
      const p = pageById.get(cell.id);
      const cd = p?.processing?.pdfParser?.result?.classificationData;
      const cls = p?.processing?.classification?.result?.[0];
      const heading = bucketed
        ? `Segments · ${cell.count ?? 1} page${(cell.count ?? 1) === 1 ? '' : 's'}`
        : cell.heading
          ? `Section · ${cell.heading}`
          : 'Page';
      setHover({
        cols: new Set([col]),
        popover: {
          heading,
          previewPageId: cell.id,
          classLabel: cls?.[0] ?? (bucketed ? cell.classification : undefined),
          classScore: cls?.[1],
          cdTitle: cd?.title,
          cdDescription: cd?.description,
          x: e.clientX,
          y: e.clientY,
        },
      });
    },
    [lin, pageById, bucketed],
  );

  const onSlotEnter = useCallback(
    (index: number, e: MouseEvent) => {
      const s = lin.slots[index];
      const cols = new Set(s.childCols);
      if (bucketed) {
        setHover({ cols, popover: { heading: `Original pages ${s.label}`, previewPageId: s.openPageId, x: e.clientX, y: e.clientY } });
        return;
      }
      if (!s.segmented) {
        const cell = lin.cells[s.firstCol];
        const p = pageById.get(cell.id);
        const cd = p?.processing?.pdfParser?.result?.classificationData;
        const cls = p?.processing?.classification?.result?.[0];
        setHover({
          cols,
          popover: {
            heading: `Original page ${s.pageNo}`,
            previewPageId: cell.id,
            classLabel: cls?.[0],
            classScore: cls?.[1],
            cdTitle: cd?.title,
            cdDescription: cd?.description,
            x: e.clientX,
            y: e.clientY,
          },
        });
        return;
      }
      setHover({
        cols,
        popover: {
          heading: `Original page ${s.pageNo} · split into ${s.childCols.length} sections`,
          previewPageId: s.parentPageId ?? lin.cells[s.firstCol].id,
          x: e.clientX,
          y: e.clientY,
        },
      });
    },
    [lin, pageById, bucketed],
  );

  const onBandEnter = useCallback(
    (index: number, e: MouseEvent) => {
      const b = lin.bands[index];
      const cols = new Set(b.cols);
      const previewPageIds = b.cols.slice(0, 3).map((c) => lin.cells[c].id);
      if (bucketed) {
        const count = b.count ?? b.cols.length;
        setHover({
          cols,
          popover: {
            heading: `Reconstructed · ${count} doc${count === 1 ? '' : 's'} · ${b.classification}`,
            previewPageId: previewPageIds[0],
            previewPageIds,
            morePages: 0,
            classLabel: b.classification,
            x: e.clientX,
            y: e.clientY,
          },
        });
        return;
      }
      const ent = entityById.get(b.reconstructedDocumentId);
      const count = b.cols.length;
      setHover({
        cols,
        popover: {
          heading: `Reconstructed · ${ent?.suggestedFilename ?? ent?.classification ?? 'document'}${count > 1 ? ` (${count} pages)` : ''}`,
          previewPageId: previewPageIds[0],
          previewPageIds,
          morePages: count - previewPageIds.length,
          classLabel: ent?.classification,
          classScore: ent?.classificationCertainty,
          x: e.clientX,
          y: e.clientY,
        },
      });
    },
    [lin, entityById, bucketed],
  );

  const onLeafOpen = useCallback((col: number, e: MouseEvent) => open(`/document-pages/${lin.cells[col].id}`, e), [lin, open]);
  const onSlotOpen = useCallback((index: number, e: MouseEvent) => open(`/document-pages/${lin.slots[index].openPageId}`, e), [lin, open]);
  const onBandOpen = useCallback(
    (index: number, e: MouseEvent) => open(`/reconstructed-documents/${lin.bands[index].reconstructedDocumentId}`, e),
    [lin, open],
  );

  if (lin.columnCount === 0) return null;

  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden">
      <div className="px-3 py-3 sm:px-4 sm:py-4 border-b border-gray-200 dark:border-zinc-700 flex items-center justify-between gap-2">
        <h2 className="text-base font-medium text-gray-900 dark:text-zinc-100">Segmentation &amp; reconstruction</h2>
        <span className="text-xs text-gray-500 dark:text-zinc-400">
          {lin.slots.length} {bucketed ? 'bucket' : 'page'}
          {lin.slots.length === 1 ? '' : 's'} · {lin.columnCount} segment
          {lin.columnCount === 1 ? '' : 's'} · {lin.bands.length} reconstructed
        </span>
      </div>

      <div className="px-3 py-3 sm:px-4 sm:py-4">
        <div className="overflow-x-auto">
          <svg
            width={width}
            height={SVG_H}
            viewBox={`0 0 ${width} ${SVG_H}`}
            style={{ minWidth: width }}
            onMouseLeave={() => setHover(null)}
          >
            {/* connectors: page slot -> its leaf cells -> band (aligned, so straight). Skipped
                when bucketed — a faint per-column grid at ~1200 columns is noise + node budget. */}
            {!bucketed &&
              geom.connectors.map((c) => <ConnectorLine key={`cn${c.col}`} cx={c.cx} dimmed={isDimmed(c.cols)} />)}

            {/* tier 1 — original pages */}
            {geom.slots.map((s, i) => (
              <SlotCell
                key={`p${i}`}
                index={i}
                dimmed={isDimmed(s.cols)}
                rectX={s.rectX}
                rectW={s.rectW}
                textX={s.textX}
                segmented={s.segmented}
                dividerXs={s.dividerXs}
                pageNo={s.pageNo}
                showText={!bucketed}
                onEnter={onSlotEnter}
                onOpen={onSlotOpen}
              />
            ))}

            {/* tier 2 — segments / leaf pages */}
            {geom.cells.map((c) => (
              <LeafCellNode
                key={`l${c.col}`}
                col={c.col}
                dimmed={isDimmed(c.cols)}
                rectX={c.rectX}
                rectW={c.rectW}
                textX={c.textX}
                isSegment={c.isSegment}
                label={c.label}
                showText={showCellText}
                onEnter={onLeafEnter}
                onOpen={onLeafOpen}
              />
            ))}

            {/* tier 3 — reconstructed docs (contiguous bands) */}
            {geom.bands.map((b, i) => (
              <BandNode
                key={`b${lin.bands[i].firstCol}`}
                index={i}
                dimmed={isDimmed(b.cols)}
                rectX={b.rectX}
                rectW={b.rectW}
                textX={b.textX}
                fill={b.fill}
                showText={b.showText}
                label={b.label}
                onEnter={onBandEnter}
                onOpen={onBandOpen}
              />
            ))}
          </svg>
          <div className="sticky left-0 mt-2 min-h-[18px] w-fit text-xs text-gray-600 dark:text-zinc-300">
            {hover?.popover.heading ?? (
              <span className="text-gray-400 dark:text-zinc-500">
                Original pages → segments → reconstructed docs. Hover to preview &amp; trace a lineage; click to open.
              </span>
            )}
          </div>
        </div>
        {lin.bands.length === 0 && (
          <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">Not reconstructed yet — showing pages and segments only.</p>
        )}
      </div>

      {hover && <LineagePopover content={hover.popover} />}
    </div>
  );
}
